import React, { useEffect, useState } from "react";
import logo from "../../../assets/Aara_logo.png";
import Select from "react-select";
import PhoneInput from "react-phone-input-2";

const AbroadCounselingForm = ({
  formData,
  setFormData,
  handleSubmit,
  isSubmitted,
}) => {
  // Error state for form validation
  const [errors, setErrors] = useState({});
  // State to track if form was attempted to be submitted
  const [formSubmitAttempted, setFormSubmitAttempted] = useState(false);

  const handleReferenceChange = (e) => {
    const { value, checked } = e.target;

    if (checked) {
      setFormData((prevState) => ({
        ...prevState,
        reference_source: value,
      }));
      // Clear validation error for this field
      setErrors((prev) => ({ ...prev, reference_source: "" }));
    } else {
      setFormData((prevState) => ({
        ...prevState,
        reference_source: "", // Uncheck and clear the reference when the checkbox is unchecked
      }));
    }
  };

  const handleChangeCountry = (e) => {
    const { value, type } = e.target;

    setFormData((prevState) => {
      let selectedCountries = Array.isArray(
        prevState.countries_for_abroad_studies
      )
        ? [...prevState.countries_for_abroad_studies]
        : [];

      if (type === "checkbox") {
        if (value === "Others") {
          // Toggle "Others" in `country`, but do not store user input here
          if (selectedCountries.includes(value)) {
            selectedCountries = selectedCountries.filter(
              (country) => country !== value
            );
            return {
              ...prevState,
              countries_for_abroad_studies: selectedCountries,
              other_countries_abroad: "",
            };
          } else {
            selectedCountries.push(value);
            return {
              ...prevState,
              countries_for_abroad_studies: selectedCountries,
            };
          }
        } else {
          // Toggle regular country selection
          if (selectedCountries.includes(value)) {
            selectedCountries = selectedCountries.filter(
              (country) => country !== value
            );
          } else {
            selectedCountries.push(value);
          }
        }

        // Clear validation error if at least one country is selected
        if (selectedCountries.length > 0) {
          setErrors((prev) => ({ ...prev, countries_for_abroad_studies: "" }));
        }

        return {
          ...prevState,
          countries_for_abroad_studies: selectedCountries,
        };
      } else {
        // Handle "Others" input text separately
        return { ...prevState, other_countries_abroad: value };
      }
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let updatedValue = value;

    // Clean up only numeric inputs for relevant fields
    if (["percentage", "gpa", "out_off"].includes(name)) {
      updatedValue = value.replace(/[^0-9.]/g, "");
    }

    setFormData((prevState) => ({
      ...prevState,
      [name]: updatedValue,
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // Month options for dropdown
  const monthOptions = [
    { value: "January", label: "January" },
    { value: "February", label: "February" },
    { value: "March", label: "March" },
    { value: "April", label: "April" },
    { value: "May", label: "May" },
    { value: "June", label: "June" },
    { value: "July", label: "July" },
    { value: "August", label: "August" },
    { value: "September", label: "September" },
    { value: "October", label: "October" },
    { value: "November", label: "November" },
    { value: "December", label: "December" },
  ];

  // Generate years from current year to the current year + 10 years
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 11 }, (_, index) => {
    const year = currentYear + index;
    return { value: year.toString(), label: year.toString() };
  });

  const currentMonth = new Date().getMonth(); // 0-11

  const generateMonthOptions = (selectedYear) => {
    if (selectedYear && parseInt(selectedYear) === currentYear) {
      return monthOptions.filter((_, index) => index >= currentMonth);
    }
    return monthOptions;
  };

  const [availableMonths, setAvailableMonths] = useState(
    generateMonthOptions(formData.intakeYear)
  );

  const updateIntake = (month, year) => {
    if (month && year) {
      const monthIndex = {
        January: 0,
        February: 1,
        March: 2,
        April: 3,
        May: 4,
        June: 5,
        July: 6,
        August: 7,
        September: 8,
        October: 9,
        November: 10,
        December: 11,
      };

      // Create a date object in UTC
      const date = new Date(Date.UTC(year, monthIndex[month], 1)); // Always set to the 1st day of the selected month

      // Convert to an ISO string format (YYYY-MM-DDTHH:MM:SSZ)
      const formattedDate = date.toISOString().split(".")[0] + "Z";

      setFormData((prevState) => ({
        ...prevState,
        intakeMonth: month,
        intakeYear: year,
        intake: formattedDate, // Save the datetime string
      }));

      // Clear validation errors for intake fields
      setErrors((prev) => ({
        ...prev,
        intakeMonth: "",
        intakeYear: "",
        intake: "",
      }));
    }
  };

  const handleMonthChange = (selectedOption) => {
    const month = selectedOption ? selectedOption.value : "";
    const selectedMonthIndex = monthOptions.findIndex((m) => m.value === month);

    if (
      formData.intakeYear &&
      parseInt(formData.intakeYear) === currentYear &&
      selectedMonthIndex < currentMonth
    ) {
      // Prevent past month selection
      return;
    }

    setFormData((prev) => ({ ...prev, intakeMonth: month }));
    updateIntake(month, formData.intakeYear);
  };

  const handleYearChange = (selectedOption) => {
    const year = selectedOption ? selectedOption.value : "";
    const updatedMonths = generateMonthOptions(year);
    setAvailableMonths(updatedMonths);

    const selectedMonthIndex = monthOptions.findIndex(
      (m) => m.value === formData.intakeMonth
    );

    if (year && parseInt(year) === currentYear) {
      if (selectedMonthIndex < currentMonth) {
        const fallbackMonth = updatedMonths[0]?.value || "";
        setFormData((prevState) => ({
          ...prevState,
          intakeYear: year,
          intakeMonth: fallbackMonth,
        }));
        updateIntake(fallbackMonth, year);
        return;
      }
    }

    setFormData((prevState) => ({
      ...prevState,
      intakeYear: year,
    }));

    updateIntake(formData.intakeMonth, year);
  };

  const handleSelectChange = (selectedOption) => {
    setFormData((prevState) => ({
      ...prevState,
      gpa_percentage: selectedOption ? selectedOption.value : "",
    }));

    // Clear validation error for this field
    setErrors((prev) => ({ ...prev, gpa_percentage: "" }));
  };

  const handlePhoneChange = (value) => {
    setFormData((prevData) => ({
      ...prevData,
      phone: value,
    }));

    // Clear validation error for this field
    setErrors((prev) => ({ ...prev, phone: "" }));
  };

  const handleAlternatePhoneChange = (value) => {
    setFormData((prevData) => ({
      ...prevData,
      alternate_phone: value,
    }));
  };

  // Options for GPA/Percentage dropdown
  const gpaOrPercentageOptions = [
    { value: "gpa", label: "GPA" },
    { value: "percentage", label: "Percentage" },
  ];

  // Form validation function
  const validateForm = () => {
    const newErrors = {};

    // Required fields validation
    if (!formData.name || formData.name.trim() === "") {
      newErrors.name = "Name is required";
    }

    if (!formData.email || formData.email.trim() === "") {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }

    if (!formData.phone || formData.phone.length < 10) {
      newErrors.phone = "Valid phone number is required";
    }

    if (
      !formData.residence_city_state ||
      formData.residence_city_state.trim() === ""
    ) {
      newErrors.residence_city_state = "Residence information is required";
    }

    if (!formData.reference_source) {
      newErrors.reference_source = "Please select a reference source";
    }

    if (
      !formData.current_education ||
      formData.current_education.trim() === ""
    ) {
      newErrors.current_education = "Current education is required";
    }

    if (
      !formData.previous_education ||
      formData.previous_education.trim() === ""
    ) {
      newErrors.previous_education = "Previous education is required";
    }

    if (!formData.gpa_percentage) {
      newErrors.gpa_percentage = "Please select GPA or Percentage";
    } else {
      if (formData.gpa_percentage === "gpa") {
        if (!formData.gpa || formData.gpa === "") {
          newErrors.gpa = "GPA is required";
        }
        if (!formData.out_off || formData.out_off === "") {
          newErrors.out_off = "Out of value is required";
        }
      } else if (formData.gpa_percentage === "percentage") {
        if (!formData.percentage || formData.percentage === "") {
          newErrors.percentage = "Percentage is required";
        }
      }
    }

    if (!formData.intended_degree || formData.intended_degree === "") {
      newErrors.intended_degree = "Please select a degree";
    }
    if (!formData.intended_courses || formData.intended_courses.trim() === "") {
      newErrors.intended_courses = "Course shortlist is required";
    }

    if (
      !formData.countries_for_abroad_studies ||
      formData.countries_for_abroad_studies.length === 0
    ) {
      newErrors.countries_for_abroad_studies =
        "Please select at least one country";
    }

    if (!formData.intakeMonth || !formData.intakeYear) {
      newErrors.intake = "Please select both month and year for intake";
    }

    return newErrors;
  };

  // Custom submit handler
  const onSubmitForm = (e) => {
    e.preventDefault();
    setFormSubmitAttempted(true);

    const formErrors = validateForm();
    setErrors(formErrors);

    if (Object.keys(formErrors).length === 0) {
      // If no errors, proceed with form submission
      handleSubmit(e);
    } else {
      // Scroll to first error
      const firstErrorField = document.querySelector(".error-message");
      if (firstErrorField) {
        firstErrorField.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  };

  if (isSubmitted) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 p-4">
        <img src={logo} alt="Aara Consultancy Logo" className="h-24 mb-6" />
        <h1 className="text-4xl font-bold text-gray-800 mb-4 text-center">
          Thank You for Submitting!
        </h1>
        <p className="text-lg text-gray-600 mb-6 text-center">
          We have received your information and will get back to you soon.
        </p>
      </div>
    );
  }

  return (
    <div className="container max-w-4xl mx-auto p-4">
      {/* Logo Section */}
      <div className="flex justify-center mb-6">
        <img
          src={logo}
          alt="Aara Consultancy Logo"
          className="h-16 sm:h-20" // Responsive logo size
        />
      </div>
      <form
        onSubmit={onSubmitForm}
        className="bg-gray-100 shadow-lg p-4 sm:p-6 rounded-lg"
      >
        <h2 className="text-xl sm:text-2xl font-semibold text-center mb-4">
          {formData.lead_type === "abroad_counseling"
            ? "Abroad Counseling Student Information Form"
            : "Profile Building Student Information Form"}
        </h2>
        <p className="text-xs sm:text-sm text-gray-500 mb-4">
          Fields marked with * are mandatory and can not be left blank!
        </p>

        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <label
              htmlFor="name"
              className="font-semibold mb-1 sm:mb-0 sm:w-1/3"
            >
              Name *
            </label>
            <div className="w-full sm:w-1/2">
              <input
                type="text"
                name="name"
                id="name"
                value={formData.name || ""}
                onChange={handleChange}
                className={`border rounded-lg p-2 w-full ${
                  errors.name && formSubmitAttempted ? "border-red-500" : ""
                }`}
              />
              {errors.name && formSubmitAttempted && (
                <p className="text-red-500 text-xs mt-1 error-message">
                  {errors.name}
                </p>
              )}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <label
              htmlFor="email"
              className="font-semibold mb-1 sm:mb-0 sm:w-1/3"
            >
              Email *
            </label>
            <div className="w-full sm:w-1/2">
              <input
                type="email"
                name="email"
                id="email"
                value={formData.email || ""}
                onChange={handleChange}
                className={`border rounded-lg p-2 w-full ${
                  errors.email && formSubmitAttempted ? "border-red-500" : ""
                }`}
              />
              {errors.email && formSubmitAttempted && (
                <p className="text-red-500 text-xs mt-1 error-message">
                  {errors.email}
                </p>
              )}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <label
              htmlFor="phone"
              className="font-semibold mb-1 sm:mb-0 sm:w-1/3"
            >
              Mobile No *
            </label>
            <div className="w-full sm:w-1/2">
              <PhoneInput
                country={"in"} // Default country India (+91)
                value={formData.phone || ""}
                onChange={handlePhoneChange}
                inputStyle={{
                  width: "100%",
                  height: "40px",
                  borderRadius: "6px",
                  borderColor:
                    errors.phone && formSubmitAttempted ? "#f56565" : "#ccc",
                  paddingLeft: "50px", // Adjust for flag
                }}
                containerStyle={{
                  width: "100%",
                }}
                dropdownStyle={{ width: "300px" }}
              />
              {errors.phone && formSubmitAttempted && (
                <p className="text-red-500 text-xs mt-1 error-message">
                  {errors.phone}
                </p>
              )}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <label
              htmlFor="alternate_phone"
              className="font-semibold mb-1 sm:mb-0 sm:w-1/3"
            >
              Alternate Contact Number (Parents)
            </label>
            <div className="w-full sm:w-1/2">
              <PhoneInput
                country={"in"} // Default country India (+91)
                value={formData.alternate_phone || ""}
                onChange={handleAlternatePhoneChange}
                inputStyle={{
                  width: "100%",
                  height: "40px",
                  borderRadius: "6px",
                  borderColor: "#ccc",
                  paddingLeft: "50px", // Adjust for flag
                }}
                containerStyle={{
                  width: "100%",
                }}
                dropdownStyle={{ width: "300px" }}
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <label
              htmlFor="residence_city_state"
              className="font-semibold mb-1 sm:mb-0 sm:w-1/3"
            >
              In which State/City/Country do you reside in? *
            </label>
            <div className="w-full sm:w-1/2">
              <input
                type="text"
                name="residence_city_state"
                id="residence_city_state"
                value={formData.residence_city_state || ""}
                onChange={handleChange}
                className={`border rounded-lg p-2 w-full ${
                  errors.residence_city_state && formSubmitAttempted
                    ? "border-red-500"
                    : ""
                }`}
              />
              {errors.residence_city_state && formSubmitAttempted && (
                <p className="text-red-500 text-xs mt-1 error-message">
                  {errors.residence_city_state}
                </p>
              )}
            </div>
          </div>

          <div className="flex flex-col md:flex-row md:justify-between">
            <label
              htmlFor="reference_source"
              className="font-semibold mb-1 md:mb-0 sm:w-1/2"
            >
              How did you get your reference? *
            </label>
            <div className="w-full md:w-1/2">
              <div
                className={`grid grid-cols-1 sm:grid-cols-2 gap-2 border rounded-lg p-2 bg-white ${
                  formSubmitAttempted && errors.reference_source
                    ? "border-red-500"
                    : ""
                }`}
              >
                {[
                  "Reference of Friend/Relative",
                  "Word of Mouth",
                  "Google",
                  "Walk in",
                  "Seminar",
                  "Others",
                ].map((option) => (
                  <label key={option} className="flex items-center">
                    <input
                      type="checkbox"
                      name="reference_source"
                      value={option}
                      onChange={handleReferenceChange}
                      checked={formData.reference_source === option}
                      className="mr-2"
                    />
                    {option}
                  </label>
                ))}
              </div>
              {errors.reference_source && formSubmitAttempted && (
                <p className="text-red-500 text-xs mt-1 error-message">
                  {errors.reference_source}
                </p>
              )}
            </div>
          </div>

          {/* Conditional rendering based on selection */}
          {formData.reference_source === "Reference of Friend/Relative" && (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <label
                htmlFor="reference_name_id"
                className="font-semibold mb-1 sm:mb-0 sm:w-1/3"
              >
                <span className="text-sm">
                  Please mention referral student name (Useful for them to claim
                  the referral amount)
                </span>
              </label>
              <div className="w-full sm:w-1/2">
                <input
                  type="text"
                  name="reference_name_id"
                  id="reference_name_id"
                  value={formData.reference_name_id || ""}
                  onChange={handleChange}
                  className="border rounded-lg p-2 w-full"
                />
              </div>
            </div>
          )}

          {formData.reference_source === "Others" && (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <label
                htmlFor="other_reference_source"
                className="font-semibold mb-1 sm:mb-0 sm:w-1/3"
              >
                If Others, Mention source of reference
              </label>
              <div className="w-full sm:w-1/2">
                <input
                  type="text"
                  name="other_reference_source"
                  id="other_reference_source"
                  value={formData.other_reference_source || ""}
                  onChange={handleChange}
                  className="border rounded-lg p-2 w-full"
                />
              </div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <label
              htmlFor="current_education"
              className="font-semibold mb-1 sm:mb-0 sm:w-1/3"
            >
              Current education *
            </label>
            <div className="w-full sm:w-1/2">
              <input
                type="text"
                name="current_education"
                id="current_education"
                value={formData.current_education || ""}
                onChange={handleChange}
                className={`border rounded-lg p-2 w-full ${
                  errors.current_education && formSubmitAttempted
                    ? "border-red-500"
                    : ""
                }`}
              />
              {errors.current_education && formSubmitAttempted && (
                <p className="text-red-500 text-xs mt-1 error-message">
                  {errors.current_education}
                </p>
              )}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <label
              htmlFor="previous_education"
              className="font-semibold mb-1 sm:mb-0 sm:w-1/3"
            >
              Previous education *
            </label>
            <div className="w-full sm:w-1/2">
              <input
                type="text"
                name="previous_education"
                id="previous_education"
                value={formData.previous_education || ""}
                onChange={handleChange}
                className={`border rounded-lg p-2 w-full ${
                  errors.previous_education && formSubmitAttempted
                    ? "border-red-500"
                    : ""
                }`}
              />
              {errors.previous_education && formSubmitAttempted && (
                <p className="text-red-500 text-xs mt-1 error-message">
                  {errors.previous_education}
                </p>
              )}
            </div>
          </div>

          {/* GPA/Percentage Dropdown */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <label
              htmlFor="gpa_percentage"
              className="font-semibold mb-1 sm:mb-0 sm:w-1/3"
            >
              Average GPA/Percentage *
            </label>
            <div className="w-full sm:w-1/2">
              <Select
                name="gpa_percentage"
                inputId="gpa_percentage"
                options={gpaOrPercentageOptions}
                onChange={handleSelectChange}
                value={gpaOrPercentageOptions.find(
                  (option) => option.value === formData.gpa_percentage
                )}
                isSearchable
                className={
                  errors.gpa_percentage && formSubmitAttempted
                    ? "select-error"
                    : ""
                }
                styles={{
                  control: (provided) => ({
                    ...provided,
                    borderColor:
                      errors.gpa_percentage && formSubmitAttempted
                        ? "#f56565"
                        : provided.borderColor,
                  }),
                }}
              />
              {errors.gpa_percentage && formSubmitAttempted && (
                <p className="text-red-500 text-xs mt-1 error-message">
                  {errors.gpa_percentage}
                </p>
              )}
            </div>
          </div>

          {/* Conditional GPA/Percentage Input */}
          {formData.gpa_percentage === "percentage" && (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <label
                htmlFor="percentage"
                className="font-semibold mb-1 sm:mb-0 sm:w-1/3"
              >
                Enter your {formData.gpa_percentage}
              </label>
              <div className="w-full sm:w-1/2">
                <div className="relative">
                  <input
                    type="number"
                    name="percentage"
                    id="percentage"
                    value={formData.percentage || ""}
                    onChange={handleChange}
                    placeholder="Enter percentage"
                    className={`border rounded-lg p-2 pr-10 w-full appearance-none focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                      errors.percentage && formSubmitAttempted
                        ? "border-red-500"
                        : ""
                    }`}
                    min="0"
                    max="100"
                    step="0.01"
                  />
                  <span className="absolute right-3 top-2.5 text-gray-500 text-sm font-medium">
                    %
                  </span>
                </div>
                {errors.percentage && formSubmitAttempted && (
                  <p className="text-red-500 text-xs mt-1 error-message">
                    {errors.percentage}
                  </p>
                )}
              </div>
            </div>
          )}

          {formData.gpa_percentage === "gpa" && (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <label
                htmlFor="gpa"
                className="font-semibold mb-1 sm:mb-0 sm:w-1/3"
              >
                Enter GPA (e.g., 5 out of 10)
              </label>
              <div className="w-full sm:w-1/2">
                <div className="flex space-x-2">
                  <div className="w-2/5">
                    <input
                      type="number"
                      name="gpa"
                      id="gpa"
                      value={formData.gpa || ""}
                      onChange={handleChange}
                      placeholder="Enter GPA"
                      className={`border rounded-lg p-2 w-full ${
                        errors.gpa && formSubmitAttempted
                          ? "border-red-500"
                          : ""
                      }`}
                      step="0.1" // Allows decimals
                      min="0" // Prevent negative numbers
                    />
                    {errors.gpa && formSubmitAttempted && (
                      <p className="text-red-500 text-xs mt-1 error-message">
                        {errors.gpa}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center">
                    <span className="mx-1 text-lg">/</span>
                  </div>
                  <div className="w-2/5">
                    <input
                      type="number"
                      name="out_off"
                      id="out_off"
                      value={formData.out_off || ""}
                      onChange={handleChange}
                      placeholder="Out of 10"
                      className={`border rounded-lg p-2 w-full ${
                        errors.out_off && formSubmitAttempted
                          ? "border-red-500"
                          : ""
                      }`}
                      min="0" // Prevent negative numbers
                    />
                    {errors.out_off && formSubmitAttempted && (
                      <p className="text-red-500 text-xs mt-1 error-message">
                        {errors.out_off}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <label
              htmlFor="intended_degree"
              className="font-semibold mb-1 sm:mb-0 sm:w-1/3"
            >
              Intended Degree *
            </label>
            <div className="w-full sm:w-1/2">
              <select
                name="intended_degree"
                id="intended_degree"
                value={formData.intended_degree || ""}
                onChange={handleChange}
                className={`border rounded-lg p-2 w-full ${
                  errors.intended_degree && formSubmitAttempted
                    ? "border-red-500"
                    : ""
                }`}
              >
                <option value="">Select a Degree</option>
                <option value="Undergraduate ">Undergraduate</option>
                <option value="Transfer">Transfer</option>
                <option value="Masters">Masters</option>
                <option value="MBA">MBA</option>
                <option value="PHD">PHD</option>
              </select>
              {errors.intended_degree && formSubmitAttempted && (
                <p className="text-red-500 text-xs mt-1 error-message">
                  {errors.intended_degree}
                </p>
              )}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:justify-between">
            <label
              htmlFor="countries_for_abroad_studies"
              className="font-semibold mb-1 sm:mb-0 sm:w-1/3"
            >
              Intended Countries (Multiple select)*
            </label>

            <div className="w-full sm:w-1/2">
              <div
                className={`grid grid-cols-2 sm:grid-cols-3 gap-2 border rounded-lg p-2 bg-white ${
                  formSubmitAttempted && errors.countries_for_abroad_studies
                    ? "border-red-500"
                    : ""
                }`}
              >
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    value="USA"
                    onChange={handleChangeCountry}
                    checked={
                      formData.countries_for_abroad_studies &&
                      formData.countries_for_abroad_studies.includes("USA")
                    }
                    className="mr-2"
                  />
                  <span className="text-sm">USA</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    value="UK"
                    onChange={handleChangeCountry}
                    checked={
                      formData.countries_for_abroad_studies &&
                      formData.countries_for_abroad_studies.includes("UK")
                    }
                    className="mr-2"
                  />
                  <span className="text-sm">UK</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    value="Australia"
                    onChange={handleChangeCountry}
                    checked={
                      formData.countries_for_abroad_studies &&
                      formData.countries_for_abroad_studies.includes(
                        "Australia"
                      )
                    }
                    className="mr-2"
                  />

                  <span className="text-sm">Australia</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    value="Canada"
                    onChange={handleChangeCountry}
                    checked={
                      formData.countries_for_abroad_studies &&
                      formData.countries_for_abroad_studies.includes("Canada")
                    }
                    className="mr-2"
                  />
                  <span className="text-sm">Canada</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    value="Germany"
                    onChange={handleChangeCountry}
                    checked={
                      formData.countries_for_abroad_studies &&
                      formData.countries_for_abroad_studies.includes("Germany")
                    }
                    className="mr-2"
                  />
                  <span className="text-sm">Germany</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    value="India"
                    onChange={handleChangeCountry}
                    checked={
                      formData.countries_for_abroad_studies &&
                      formData.countries_for_abroad_studies.includes("India")
                    }
                    className="mr-2"
                  />
                  <span className="text-sm">India</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    value="Ireland"
                    onChange={handleChangeCountry}
                    checked={
                      formData.countries_for_abroad_studies &&
                      formData.countries_for_abroad_studies.includes("Ireland")
                    }
                    className="mr-2"
                  />
                  <span className="text-sm">Ireland</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    value="Newzealand"
                    onChange={handleChangeCountry}
                    checked={
                      formData.countries_for_abroad_studies &&
                      formData.countries_for_abroad_studies.includes(
                        "Newzealand"
                      )
                    }
                    className="mr-2"
                  />
                  <span className="text-sm">New Zealand</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    value="Others"
                    onChange={handleChangeCountry}
                    checked={
                      formData.countries_for_abroad_studies &&
                      formData.countries_for_abroad_studies.includes("Others")
                    }
                    className="mr-2"
                  />
                  <span className="text-sm">Others</span>
                </label>
              </div>
              {errors.countries_for_abroad_studies && formSubmitAttempted && (
                <p className="text-red-500 text-xs mt-1 error-message">
                  {errors.countries_for_abroad_studies}
                </p>
              )}
            </div>
          </div>

          {/* Conditionally render the input field if "Others" is checked */}
          {formData.countries_for_abroad_studies &&
            formData.countries_for_abroad_studies.includes("Others") && (
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <label
                  htmlFor="other_countries_abroad"
                  className="font-semibold mb-1 sm:mb-0 sm:w-1/3"
                >
                  Other Countries For Abroad studies (If Applicable)
                </label>
                <div className="w-full sm:w-1/2">
                  <input
                    type="text"
                    name="other_countries_abroad"
                    id="other_countries_abroad"
                    value={formData.other_countries_abroad || ""}
                    onChange={handleChangeCountry}
                    className="border rounded-lg p-2 w-full"
                  />
                </div>
              </div>
            )}

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <label
              htmlFor="intended_courses"
              className="font-semibold mb-1 sm:mb-0 sm:w-1/3"
            >
              Intended Courses Shortlisted *
            </label>
            <div className="w-full sm:w-1/2">
              <input
                type="text"
                name="intended_courses"
                id="intended_courses"
                value={formData.intended_courses || ""}
                onChange={handleChange}
                className={`border rounded-lg p-2 w-full ${
                  errors.intended_courses && formSubmitAttempted
                    ? "border-red-500"
                    : ""
                }`}
              />
              {errors.intended_courses && formSubmitAttempted && (
                <p className="text-red-500 text-xs mt-1 error-message">
                  {errors.intended_courses}
                </p>
              )}
            </div>
          </div>

          {/* Intake Month and Year */}
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between">
            <label
              htmlFor="intake"
              className="font-semibold mb-1 sm:mb-0 sm:w-1/3"
            >
              Intake you do wish to go for *
            </label>
            <div className="w-full sm:w-1/2">
              <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3">
                <div className="w-full sm:w-1/2">
                  <label
                    htmlFor="intakeMonth"
                    className="text-sm font-medium mb-1 block"
                  >
                    Month
                  </label>
                  <Select
                    name="intakeMonth"
                    inputId="intakeMonth"
                    options={availableMonths}
                    onChange={handleMonthChange}
                    value={monthOptions.find(
                      (option) => option.value === formData.intakeMonth
                    )}
                    isSearchable
                    isClearable
                    isMulti={false}
                    className="react-select-container"
                    styles={{
                      control: (provided) => ({
                        ...provided,
                        borderColor:
                          errors.intake && formSubmitAttempted
                            ? "#f56565"
                            : provided.borderColor,
                      }),
                    }}
                  />
                </div>
                <div className="w-full sm:w-1/2">
                  <label
                    htmlFor="intakeYear"
                    className="text-sm font-medium mb-1 block"
                  >
                    Year
                  </label>
                  <Select
                    name="intakeYear"
                    inputId="intakeYear"
                    options={yearOptions}
                    onChange={handleYearChange}
                    value={yearOptions.find(
                      (option) => option.value === formData.intakeYear
                    )}
                    isSearchable
                    isClearable
                    isMulti={false}
                    className="react-select-container"
                    styles={{
                      control: (provided) => ({
                        ...provided,
                        borderColor:
                          errors.intake && formSubmitAttempted
                            ? "#f56565"
                            : provided.borderColor,
                      }),
                    }}
                  />
                </div>
              </div>
              {errors.intake && formSubmitAttempted && (
                <p className="text-red-500 text-xs mt-1 error-message">
                  {errors.intake}
                </p>
              )}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between">
            <label
              htmlFor="other_perfrences"
              className="font-semibold mb-1 sm:mb-0 sm:w-1/3"
            >
              Other information/preference (Please limit your response to 500
              words or 3000 characters.)
            </label>
            <div className="w-full sm:w-1/2">
              <textarea
                name="other_perfrences"
                id="other_perfrences"
                value={formData.other_perfrences || ""}
                onChange={handleChange}
                className="border rounded-lg p-2 w-full h-24"
                maxLength={3000}
              />
              <p className="text-xs text-gray-500 mt-1">
                {formData.other_perfrences
                  ? formData.other_perfrences.length
                  : 0}
                /3000 characters
              </p>
            </div>
          </div>

          <div className="flex justify-center mt-6">
            <button
              type="submit"
              className="bg-red-500 hover:bg-red-600 text-white py-2 px-6 rounded-lg transition duration-300 ease-in-out transform hover:scale-105"
            >
              Submit
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default AbroadCounselingForm;
